import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    CardContent,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Menu,
    ListItemIcon,
    ListItemText,
    Divider,
    CssBaseline,
    Chip,
    Grid,
    Checkbox,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Button } from "@mui/material"; // if Button not already imported
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SearchIcon from "@mui/icons-material/Search";
import dividerImg from "../../../assets/divider.png";
import { useUser } from "../../../contexts/UserContext";
import { formatDateToDDMMYYYY } from "../../../util";
import ItemTableUpdateStock from "./Modal/ItemTableUpdateStock";
import ItemTableStockHistory from "./Modal/ItemTableStockHistory";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ItemTableUpdate from "./Modal/ItemTableUpdate";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

const formatDate = (d) => {
    return formatDateToDDMMYYYY(d);
};

const STORE_MERGE_KEY = "faiz_store_merge_items";

function getMergeSelectionFromStorage() {
    try {
        const raw = localStorage.getItem(STORE_MERGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((x) => x && Number(x.id)) : [];
    } catch {
        return [];
    }
}

function setMergeSelectionInStorage(items) {
    localStorage.setItem(STORE_MERGE_KEY, JSON.stringify(items));
}

export default function StoreItemTable() {
    const { token } = useUser();
    const base = process.env.REACT_APP_API_BASE || "https://api.fmb52.com/api";

    const [items, setItems] = useState([]);
    const [loadingFirst, setLoadingFirst] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [stockModalOpen, setStockModalOpen] = useState(false);
    const [stockRow, setStockRow] = useState(null);

    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyRow, setHistoryRow] = useState(null);

    const [updateOpen, setUpdateOpen] = useState(false);
    const [updateRow, setUpdateRow] = useState(null);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteRow, setDeleteRow] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [deactivateOpen, setDeactivateOpen] = useState(false);
    const [deactivateRow, setDeactivateRow] = useState(null);
    const [deactivating, setDeactivating] = useState(false);

    // Bulk selection (checkboxes)
    const [selectedIds, setSelectedIds] = useState([]);

    // Merge: selection persisted in localStorage (CSV of ids for API; we store JSON for id+name)
    const [mergeSelection, setMergeSelection] = useState(() => getMergeSelectionFromStorage());

    const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
    const [mergeSubmitting, setMergeSubmitting] = useState(false);
    const [mergeForm, setMergeForm] = useState({ name: "", unit: "", category: "" });

    const [bulkActionProgress, setBulkActionProgress] = useState(false);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    const [bulkDeactivateConfirmOpen, setBulkDeactivateConfirmOpen] = useState(false);

    const MERGE_UNIT_OPTIONS = ["LTR", "PKT", "BOX", "KGS", "NOS", "BOTTLES"];
    const MERGE_CATEGORY_OPTIONS = [
        "GRAINS & PULSES",
        "SPICES & DRY FRUITS",
        "OIL & GHEE",
        "MEATS",
        "VEGETABLES / FRUITS",
        "CLEANING MATERIAL",
        "PACKAGING",
        "SHERBET",
        "ACHAAR",
        "BISCUIT",
        "OTHERS",
    ];

    // Filters
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [alphabet, setAlphabet] = useState("");

    const LIMIT = 30;
    const [offset, setOffset] = useState(0);

    const containerRef = useRef(null);

    // Action menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);

    const headers = useMemo(
        () => ({
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
        }),
        [token]
    );

    const buildUrl = ({ nextOffset }) => {
        // NOTE: backend should support these params.
        const params = new URLSearchParams();
        params.set("limit", String(LIMIT));
        params.set("offset", String(nextOffset));

        if (search?.trim()) params.set("search", search.trim());
        if (category) params.set("category", category);
        // alphabet: only first character, must be A-Z / a-z
        if (alphabet?.trim()) {
            const letter = String(alphabet).trim().charAt(0);
            if (/^[A-Za-z]$/.test(letter)) params.set("alphabet", letter);
        }

        return `${base}/store-items?${params.toString()}`;
    };

    const fetchPage = async ({ reset = false } = {}) => {
        if (!token) return;

        reset ? setLoadingFirst(true) : setLoadingMore(true);

        try {
            const nextOffset = reset ? 0 : offset;
            const url = buildUrl({ nextOffset });

            const resp = await fetch(url, { method: "GET", headers });
            if (!resp.ok) throw new Error("Failed to fetch store items");

            const json = await resp.json();
            const rows = Array.isArray(json?.data) ? json.data : [];

            if (reset) {
                setItems(rows);
                setOffset(rows.length);
            } else {
                setItems((prev) => {
                    const seen = new Set(prev.map((x) => x.id));
                    const merged = [...prev];
                    for (const r of rows) if (!seen.has(r.id)) merged.push(r);
                    return merged;
                });
                setOffset((prev) => prev + rows.length);
            }

            setHasMore(rows.length === LIMIT);
        } catch (e) {
            console.error("StoreItemTable error:", e);
            if (reset) setItems([]);
            setHasMore(false);
        } finally {
            setLoadingFirst(false);
            setLoadingMore(false);
        }
    };

    const handleExportExcel = () => {
        const params = new URLSearchParams();
        if (search?.trim()) params.set("search", search.trim());
        if (category) params.set("category", category);
        if (alphabet?.trim()) {
            const letter = String(alphabet).trim().charAt(0);
            if (/^[A-Za-z]$/.test(letter)) params.set("alphabet", letter);
        }

        const url = `${base}/store-items/export/excel?${params.toString()}`;

        // simplest: open download in new tab
        window.open(url, "_blank");
    };

    const confirmDelete = async () => {
        if (!deleteRow?.id || !token) return;

        setDeleting(true);
        try {
            const resp = await fetch(`${base}/store-items/${deleteRow.id}`, {
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await resp.json().catch(() => ({}));

            if (!resp.ok || data?.success === false) {
                alert(data?.message || "Delete failed");
                return;
            }

            // ✅ remove from UI immediately
            setItems((prev) => prev.filter((x) => x.id !== deleteRow.id));

            // (optional) refresh from server to stay accurate
            // handleRefresh();  // if you have refresh function

            setDeleteOpen(false);
            setDeleteRow(null);
        } catch (e) {
            console.error("Delete item error:", e);
            alert("Something went wrong");
        } finally {
            setDeleting(false);
        }
    };

    const onHistory = (row) => {
        setHistoryRow(row);
        setHistoryOpen(true);
        closeMenu();
    };
    const onUpdate = (row) => {
        setUpdateRow(row);
        setUpdateOpen(true);
        closeMenu();
    };
    // Placeholder actions (wire APIs later)
    const onUpdateStock = (row) => {
        setStockRow(row);
        setStockModalOpen(true);
        closeMenu();
    };

    const onDelete = (row) => {
        setDeleteRow(row);
        setDeleteOpen(true);
        closeMenu();
    };

    const onDeactivate = (row) => {
        setDeactivateRow(row);
        setDeactivateOpen(true);
        closeMenu();
    };

    const confirmDeactivate = async () => {
        if (!deactivateRow?.id || !token) return;

        setDeactivating(true);
        try {
            const resp = await fetch(`${base}/store-items/${deactivateRow.id}/deactivate`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await resp.json().catch(() => ({}));

            if (!resp.ok || data?.success === false) {
                alert(data?.message || "Deactivate failed");
                return;
            }

            setDeactivateOpen(false);
            setDeactivateRow(null);
            setHasMore(true);
            setOffset(0);
            if (containerRef.current) containerRef.current.scrollTop = 0;
            fetchPage({ reset: true });
        } catch (e) {
            console.error("Deactivate item error:", e);
            alert("Something went wrong");
        } finally {
            setDeactivating(false);
        }
    };


    // Initial load
    useEffect(() => {
        if (token) {
            setHasMore(true);
            setOffset(0);
            if (containerRef.current) containerRef.current.scrollTop = 0;
            fetchPage({ reset: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, base]);

    // Re-load on filter change (debounced search)
    useEffect(() => {
        if (!token) return;

        const t = setTimeout(() => {
            setHasMore(true);
            setOffset(0);
            if (containerRef.current) containerRef.current.scrollTop = 0;
            fetchPage({ reset: true });
        }, 350);

        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, category, alphabet]);

    const handleScroll = (e) => {
        const el = e.currentTarget;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;

        if (nearBottom && hasMore && !loadingFirst && !loadingMore) {
            fetchPage({ reset: false });
        }
    };

    // Menu handlers
    const openMenu = (event, row) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const addToMerge = (row) => {
        if (!row?.id) return;
        const current = getMergeSelectionFromStorage();
        if (current.some((x) => Number(x.id) === Number(row.id))) {
            closeMenu();
            return;
        }
        const next = [...current, { id: row.id, name: row.name || `Item ${row.id}` }];
        setMergeSelectionInStorage(next);
        setMergeSelection(next);
        closeMenu();
    };

    const removeFromMerge = (id) => {
        const current = getMergeSelectionFromStorage().filter((x) => Number(x.id) !== Number(id));
        setMergeSelectionInStorage(current);
        setMergeSelection(current);
    };

    const clearMergeSelection = () => {
        setMergeSelectionInStorage([]);
        setMergeSelection([]);
    };

    const handleSelectAll = (checked) => {
        setSelectedIds(checked ? items.map((it) => it.id) : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const clearSelection = () => setSelectedIds([]);

    const allSelected = items.length > 0 && selectedIds.length === items.length;
    const someSelected = selectedIds.length > 0;

    const handleBulkAddToMerge = () => {
        const toAdd = items.filter((it) => selectedIds.includes(it.id));
        const current = getMergeSelectionFromStorage();
        const existingIds = new Set(current.map((x) => x.id));
        const next = [...current];
        for (const it of toAdd) {
            if (!existingIds.has(it.id)) {
                existingIds.add(it.id);
                next.push({ id: it.id, name: it.name || `Item ${it.id}` });
            }
        }
        setMergeSelectionInStorage(next);
        setMergeSelection(next);
        setSelectedIds([]);
    };

    const handleBulkDeleteConfirm = async () => {
        if (selectedIds.length === 0 || !token) return;
        setBulkActionProgress(true);
        try {
            for (const id of selectedIds) {
                const resp = await fetch(`${base}/store-items/${id}`, {
                    method: "DELETE",
                    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
                });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok && data?.success !== false) {
                    // continue with others; could collect errors
                }
            }
            setBulkDeleteConfirmOpen(false);
            setSelectedIds([]);
            setHasMore(true);
            setOffset(0);
            if (containerRef.current) containerRef.current.scrollTop = 0;
            fetchPage({ reset: true });
        } catch (e) {
            console.error("Bulk delete error:", e);
            alert("Something went wrong");
        } finally {
            setBulkActionProgress(false);
        }
    };

    const handleBulkDeactivateConfirm = async () => {
        if (selectedIds.length === 0 || !token) return;
        setBulkActionProgress(true);
        try {
            for (const id of selectedIds) {
                const resp = await fetch(`${base}/store-items/${id}/deactivate`, {
                    method: "POST",
                    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
                });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok && data?.success !== false) {
                    // continue
                }
            }
            setBulkDeactivateConfirmOpen(false);
            setSelectedIds([]);
            setHasMore(true);
            setOffset(0);
            if (containerRef.current) containerRef.current.scrollTop = 0;
            fetchPage({ reset: true });
        } catch (e) {
            console.error("Bulk deactivate error:", e);
            alert("Something went wrong");
        } finally {
            setBulkActionProgress(false);
        }
    };

    const openMergeDialog = () => {
        const list = getMergeSelectionFromStorage();
        const first = list[0];
        setMergeForm({
            name: first?.name || "",
            unit: MERGE_UNIT_OPTIONS[0] || "",
            category: MERGE_CATEGORY_OPTIONS[0] || "",
        });
        setMergeDialogOpen(true);
    };

    const handleMergeSubmit = async () => {
        const list = getMergeSelectionFromStorage();
        if (list.length < 2) {
            alert("Select at least 2 items to merge.");
            return;
        }
        const { name, unit, category } = mergeForm;
        if (!name?.trim()) return alert("Name is required");
        if (!unit) return alert("Unit is required");
        if (!category) return alert("Category is required");

        setMergeSubmitting(true);
        try {
            const productIds = list.map((x) => x.id).join(",");
            const resp = await fetch(`${base}/merge`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    product_ids: productIds,
                    name: name.trim(),
                    unit,
                    category,
                }),
            });

            const data = await resp.json().catch(() => ({}));

            if (resp.ok && data?.success !== false) {
                clearMergeSelection();
                setMergeDialogOpen(false);
                setHasMore(true);
                setOffset(0);
                if (containerRef.current) containerRef.current.scrollTop = 0;
                fetchPage({ reset: true });
            } else {
                alert(data?.message || "Merge failed");
            }
        } catch (e) {
            console.error("Merge items error:", e);
            alert("Something went wrong");
        } finally {
            setMergeSubmitting(false);
        }
    };

    // Category options derived from loaded items (for now)
    const categoryOptions = useMemo(() => {
        const set = new Set();
        for (const it of items) {
            if (it?.category) set.add(String(it.category));
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [items]);

    return (
        <>
            <CssBaseline />
            <Box
                sx={{
                    pt: 2,
                    pb: 3,
                    pl: 3,
                    pr: 3,
                    mr: 2,
                    ml: 2,
                    mb: 1,
                    backgroundColor: "#fff",
                    border: "1px solid #F4EBD0",
                    borderRadius: 2,
                    boxShadow: 1,
                }}
            >
                {/* Header + Filters */}
                <Box
                    sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 1.5,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Store Items
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 1.5,
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                        }}
                    >
                        <TextField
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setAlphabet("");
                            }}
                            size="small"
                            placeholder="Search items..."
                            sx={{ width: { xs: "100%", sm: 260 } }}
                            InputProps={{
                                startAdornment: (
                                    <Box sx={{ display: "flex", alignItems: "center", mr: 1, color: "text.secondary" }}>
                                        <SearchIcon fontSize="small" />
                                    </Box>
                                ),
                            }}
                        />

                        <FormControl size="small" sx={{ width: { xs: "100%", sm: 220 } }}>
                            <InputLabel id="store-items-category-label">Category</InputLabel>
                            <Select
                                labelId="store-items-category-label"
                                value={category}
                                label="Category"
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>

                                {categoryOptions.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={handleExportExcel}
                            startIcon={<FileDownloadOutlinedIcon />}
                            sx={{ height: 40 }}
                        >
                            Export Excel
                        </Button>

                    </Box>
                </Box>

                {/* Alphabet filter row */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                        mt: 1.5,
                        mb: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
                        A–Z:
                    </Typography>
                    {["All", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")].map((letter) => {
                        const isAll = letter === "All";
                        const isSelected = isAll ? !alphabet : alphabet.toUpperCase() === letter;
                        return (
                            <Button
                                key={letter}
                                variant={isSelected ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setAlphabet(isAll ? "" : letter)}
                                sx={{
                                    minWidth: 28,
                                    minHeight: 28,
                                    p: 0,
                                    fontSize: "0.75rem",
                                }}
                            >
                                {letter}
                            </Button>
                        );
                    })}
                </Box>

                {/* Divider */}
                <Box
                    sx={{
                        width: "calc(100% + 32px)",
                        ml: "-16px",
                        mr: "-16px",
                        height: { xs: 10, sm: 15 },
                        backgroundImage: `url(${dividerImg})`,
                        backgroundSize: "contain",
                        backgroundRepeat: "repeat-x",
                        backgroundPosition: "center",
                        my: 2,
                    }}
                />

                {/* Merge selection bar */}
                {mergeSelection.length > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: "action.hover",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Selected for merge ({mergeSelection.length} item{mergeSelection.length !== 1 ? "s" : ""})
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                            {mergeSelection.map((item) => (
                                <Chip
                                    key={item.id}
                                    label={item.name}
                                    size="small"
                                    onDelete={() => removeFromMerge(item.id)}
                                />
                            ))}
                            <Button
                                variant="contained"
                                size="small"
                                disabled={mergeSelection.length < 2}
                                onClick={openMergeDialog}
                                startIcon={<MergeTypeIcon />}
                                sx={{ ml: 0.5 }}
                            >
                                Merge items
                            </Button>
                            <Button variant="outlined" size="small" onClick={clearMergeSelection}>
                                Clear
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Bulk actions bar */}
                {someSelected && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: "primary.light",
                            border: "1px solid",
                            borderColor: "primary.main",
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {selectedIds.length} item{selectedIds.length !== 1 ? "s" : ""} selected
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                            <Button
                                variant="contained"
                                size="small"
                                color="error"
                                disabled={bulkActionProgress}
                                startIcon={<DeleteOutlineOutlinedIcon />}
                                onClick={() => setBulkDeleteConfirmOpen(true)}
                            >
                                Delete
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                disabled={bulkActionProgress}
                                startIcon={<ToggleOffOutlinedIcon />}
                                onClick={() => setBulkDeactivateConfirmOpen(true)}
                            >
                                De-activate
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                disabled={bulkActionProgress}
                                startIcon={<PlaylistAddIcon />}
                                onClick={handleBulkAddToMerge}
                            >
                                Add to merge
                            </Button>
                            <Button variant="outlined" size="small" onClick={clearSelection} disabled={bulkActionProgress}>
                                Clear selection
                            </Button>
                        </Box>
                    </Box>
                )}

                <CardContent sx={{ pt: 0, flex: 1, display: "flex", flexDirection: "column" }}>
                    {loadingFirst ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            ref={containerRef}
                            onScroll={handleScroll}
                            sx={{
                                boxShadow: 0,
                                borderRadius: 2,
                                flex: 1,
                                maxHeight: { xs: 520, md: 560 },
                                overflowY: "auto",
                                "&::-webkit-scrollbar": { width: "8px" },
                                "&::-webkit-scrollbar-thumb": { backgroundColor: "transparent", borderRadius: "10px" },
                                "&:hover::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.25)" },
                            }}
                        >
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" sx={{ width: 48 }}>
                                            <Checkbox
                                                indeterminate={someSelected && !allSelected}
                                                checked={allSelected}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                size="small"
                                                disabled={items.length === 0}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Last Purchase</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Current Stock</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "right", width: 80 }}>
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                No items found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((it) => (
                                            <TableRow
                                                key={it.id}
                                                hover
                                                selected={selectedIds.includes(it.id)}
                                                sx={{
                                                    transition: "0.2s",
                                                    "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedIds.includes(it.id)}
                                                        onChange={() => handleSelectOne(it.id)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{it.name}</TableCell>
                                                <TableCell sx={{ color: "text.secondary" }}>{it.category || "-"}</TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {it.last_purchase ?? "-"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(it.date || it.current_stock_date || it.updated_at)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ textAlign: "right", fontWeight: 700 }}>
                                                    {it.current_stock ?? 0}
                                                </TableCell>

                                                <TableCell sx={{ textAlign: "right" }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => openMenu(e, it)}
                                                        aria-label="more"
                                                    >
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}

                                    {loadingMore && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                                                <CircularProgress size={22} />
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {!hasMore && items.length > 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 2, color: "text.secondary" }}>
                                                End of list
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>

                {/* Action Menu */}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={closeMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem onClick={() => onUpdateStock(menuRow)}>
                        <ListItemIcon>
                            <Inventory2OutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Update Current Stock</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={() => onUpdate(menuRow)}>
                        <ListItemIcon>
                            <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Update</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={() => onHistory(menuRow)}>
                        <ListItemIcon>
                            <HistoryOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>History</ListItemText>
                    </MenuItem>

                    {menuRow && menuRow.is_active !== 0 && (
                        <MenuItem onClick={() => onDeactivate(menuRow)}>
                            <ListItemIcon>
                                <ToggleOffOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>De-activate item</ListItemText>
                        </MenuItem>
                    )}

                    <MenuItem onClick={() => addToMerge(menuRow)}>
                        <ListItemIcon>
                            <PlaylistAddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Add to merge</ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem
                        onClick={() => onDelete(menuRow)}
                        sx={{ color: "error.main" }}
                    >
                        <ListItemIcon sx={{ color: "error.main" }}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </Menu>

            </Box>

            <ItemTableUpdateStock
                open={stockModalOpen}
                onClose={() => setStockModalOpen(false)}
                item={stockRow}
                token={token}
                onSuccess={() => {
                    // ✅ reload table after update
                    setHasMore(true);
                    setOffset(0);
                    if (containerRef.current) containerRef.current.scrollTop = 0;
                    fetchPage({ reset: true });
                }}
            />

            <ItemTableUpdate
                open={updateOpen}
                onClose={() => {
                    setUpdateOpen(false);
                    setUpdateRow(null);
                }}
                item={updateRow}
                token={token}
                base={base}
                onSuccess={() => {
                    // Do not refresh table or scroll to top after update
                }}
            />

            <ItemTableStockHistory
                open={historyOpen}
                onClose={() => {
                    setHistoryOpen(false);
                    setHistoryRow(null);
                }}
                item={historyRow}
                token={token}
                base={base}
            />

            <Dialog
                open={deleteOpen}
                onClose={() => (!deleting ? setDeleteOpen(false) : null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 800 }}>Confirm Delete</DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        Are you sure you want to delete{" "}
                        <b>{deleteRow?.name || "this item"}</b>?
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="outlined"
                        disabled={deleting}
                        onClick={() => {
                            setDeleteOpen(false);
                            setDeleteRow(null);
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        onClick={confirmDelete}
                        sx={{ minWidth: 120 }}
                    >
                        {deleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deactivateOpen}
                onClose={() => (!deactivating ? setDeactivateOpen(false) : null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 800 }}>De-activate item</DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        Are you sure you want to de-activate{" "}
                        <b>{deactivateRow?.name || "this item"}</b>? It will no longer be active in the store.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="outlined"
                        disabled={deactivating}
                        onClick={() => {
                            setDeactivateOpen(false);
                            setDeactivateRow(null);
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={deactivating}
                        onClick={confirmDeactivate}
                        sx={{ minWidth: 120 }}
                    >
                        {deactivating ? <CircularProgress size={20} color="inherit" /> : "De-activate"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk delete confirm */}
            <Dialog
                open={bulkDeleteConfirmOpen}
                onClose={() => (!bulkActionProgress ? setBulkDeleteConfirmOpen(false) : null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 800 }}>Delete selected items</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        Delete {selectedIds.length} item{selectedIds.length !== 1 ? "s" : ""}? This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button variant="outlined" disabled={bulkActionProgress} onClick={() => setBulkDeleteConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" disabled={bulkActionProgress} onClick={handleBulkDeleteConfirm} sx={{ minWidth: 120 }}>
                        {bulkActionProgress ? <CircularProgress size={20} color="inherit" /> : "Delete all"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk deactivate confirm */}
            <Dialog
                open={bulkDeactivateConfirmOpen}
                onClose={() => (!bulkActionProgress ? setBulkDeactivateConfirmOpen(false) : null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 800 }}>De-activate selected items</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        De-activate {selectedIds.length} item{selectedIds.length !== 1 ? "s" : ""}?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button variant="outlined" disabled={bulkActionProgress} onClick={() => setBulkDeactivateConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={bulkActionProgress} onClick={handleBulkDeactivateConfirm} sx={{ minWidth: 120 }}>
                        {bulkActionProgress ? <CircularProgress size={20} color="inherit" /> : "De-activate all"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Merge items dialog */}
            <Dialog
                open={mergeDialogOpen}
                onClose={() => (!mergeSubmitting ? setMergeDialogOpen(false) : null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 800 }}>Merge items</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Set the name, unit and category for the merged item. The first selected item will be kept; others will be merged into it and removed.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={mergeForm.name}
                                onChange={(e) => setMergeForm((f) => ({ ...f, name: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Unit</InputLabel>
                                <Select
                                    value={mergeForm.unit}
                                    label="Unit"
                                    onChange={(e) => setMergeForm((f) => ({ ...f, unit: e.target.value }))}
                                >
                                    {MERGE_UNIT_OPTIONS.map((u) => (
                                        <MenuItem key={u} value={u}>
                                            {u}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={mergeForm.category}
                                    label="Category"
                                    onChange={(e) => setMergeForm((f) => ({ ...f, category: e.target.value }))}
                                >
                                    {MERGE_CATEGORY_OPTIONS.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="outlined"
                        disabled={mergeSubmitting}
                        onClick={() => setMergeDialogOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={mergeSubmitting}
                        onClick={handleMergeSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {mergeSubmitting ? <CircularProgress size={20} color="inherit" /> : "Merge"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
